import { BufferTracker, Product } from '@bufferapp/buffer-tracking-browser-ts';
import { AnyAction } from 'redux';
import { actionTypes as orgActionTypes } from '~/organizations/reducer';
import { actionTypes as composerPopoverActionTypes } from '~/composer-popover/reducer';

import { movePostToDrafts } from '~/post/thunks';
import { selectPostById as selectPublishingPostById } from '~/post/slice';
import { selectPostById as selectRpcPostById } from '~/queue/selectors';
import { PostEntity } from '~/post/PostEntity';
import { selectCurrentOrganizationId } from '~/organizations/selectors';
import { SEGMENT_NAMES } from '~/constants';
import { actionTypes as campaignActionType } from '~/campaign/reducer';
import getCtaProperties from './utils/CtaStrings';
import { actions, actionTypes } from './actions';

let eventQueue: AnyAction[] = [];

export default ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    const { analytics } = window as Window &
      typeof globalThis & {
        analytics: unknown;
        PRODUCT_TRACKING_KEY: string;
        CLIENT_NAME: string;
      };

    next(action);
    const state = getState();
    switch (action.type) {
      case actionTypes.INIT:
        if (analytics) {
          analytics.identify(action.userId, action.payload);
        }
        break;
      case actionTypes.TRACK_EVENT: {
        const orgId = state.organizations.selected?.id;

        if (orgId) {
          if (analytics) {
            analytics.track(action.eventName, {
              ...action.payload,
              product: window.PRODUCT_TRACKING_KEY,
              clientName: window.CLIENT_NAME,
              organizationId: orgId,
            });
          }
        } else {
          eventQueue.push(action);
        }

        break;
      }
      case orgActionTypes.ORGANIZATION_SELECTED: {
        try {
          if (analytics) {
            analytics.group(action.selected.id, {
              organizationId: action.selected.id,
              organizationName: action.selected.name,
            });
          }
        } catch (err) {
          // no-op
        }
        eventQueue.forEach((event) => {
          dispatch(
            actions.trackEvent(event.eventName, {
              ...event.payload,
            })
          );
        });

        eventQueue = [];
        break;
      }
      case actionTypes.PAGE_CHANGE:
        // currently using track event for page change logic, leaving here for now
        if (analytics) {
          analytics.page(action.pageName, {
            product: window.PRODUCT_TRACKING_KEY,
            ...action.payload,
          });
        }
        break;

      case composerPopoverActionTypes.OPEN_COMPOSER: {
        if (action.cta) {
          const ctaProperties = getCtaProperties(action.cta);
          dispatch(actions.trackEvent('Composer Opened', ctaProperties));
        }
        break;
      }
      case `${movePostToDrafts.pending.type}`:
      case `${movePostToDrafts.rejected.type}`: {
        const { postId } = action.meta.arg;

        // First try find the post in the post state,
        // if not found try to check the queue state
        let post = selectPublishingPostById(state, postId);
        if (!post) post = selectRpcPostById(postId)(state);
        if (!post) {
          // eslint-disable-next-line no-console
          console.error(`Could not retrieve post at id: ${postId}`);
          return;
        }

        const channelName = PostEntity.getChannelName(post);
        const channelId = PostEntity.getChannelId(post);
        const organizationId = selectCurrentOrganizationId(state);
        const trackingData = {
          product: Product.Publish,
          clientName: 'publishWeb',
          channel: channelName,
          channelId,
          postId,
          organizationId,
        };

        if (movePostToDrafts.pending.match(action)) {
          BufferTracker.postMadeDraft(trackingData);
        } else if (movePostToDrafts.rejected.match(action)) {
          BufferTracker.postMadeDraftFailed(trackingData);
        }
        break;
      }
      case campaignActionType.CREATE_POST:
      case campaignActionType.CREATE_DRAFT: {
        const { ctaLocation } = action;
        const organizationId = selectCurrentOrganizationId(state);
        const isDraft = action.type === actionTypes.CREATE_DRAFT;

        BufferTracker.composerOpened({
          product: 'publish',
          clientName: 'publishWeb',
          organizationId,
          cta: isDraft
            ? SEGMENT_NAMES.CAMPAIGN_CREATE_DRAFT
            : SEGMENT_NAMES.CAMPAIGN_CREATE_POST,
          ctaApp: 'publish',
          ctaView: 'publishCampaignView',
          ctaLocation,
          ctaButton: isDraft ? 'createDraft' : 'createPost',
        });
        break;
      }
      default:
        break;
    }
  };
